import {AttrsSchema, Component} from 'panel';

import * as Focusable from 'common/util/focusable';

export interface BaseButtonAttrs {
  disabled?: boolean;
  elref?: string;
  'not-focusable'?: boolean;
  readonly?: boolean;
  'style-override'?: string;
}

export const baseButtonParams = {
  disabled: Boolean,
  elref: String,
  'style-override': String,
};

export class ButtonBase<
  AttrsT,
  ParamT extends Record<string, unknown> = Record<string, unknown>,
  ContextRegistryT = unknown,
> extends Component<EmptyObj, BaseButtonAttrs & AttrsT, unknown, unknown, ContextRegistryT, ParamT> {
  static get attrsSchema(): AttrsSchema<BaseButtonAttrs> {
    return {
      ...super.attrsSchema,
      disabled: `boolean`,
      elref: `string`,
      'not-focusable': `boolean`,
      readonly: `boolean`,
    };
  }

  get config() {
    return {
      defaultState: {},
      template: () => null,
      useShadowDom: true,
      helpers: {},
    };
  }

  get disabled() {
    return this.attr(`disabled`);
  }

  set disabled(disabled) {
    if (disabled) {
      this.setAttribute(`disabled`, ``);
    } else {
      this.removeAttribute(`disabled`);
    }
  }

  attributeChangedCallback(attr: string, oldValue: string | null, newValue: string | null) {
    super.attributeChangedCallback(attr, oldValue, newValue);
    Focusable.handleAttributeChangedCallback(this, attr);
  }

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute(`role`, `button`);
    this.addEventListener(`keydown`, this.handleKeyDown);
    this.addEventListener(`click`, this.handleDisabledClick, {capture: true});

    Focusable.handleConnectedCallback(this);
  }

  disconnectedCallback() {
    this.removeEventListener(`keydown`, this.handleKeyDown);
    this.removeEventListener(`click`, this.handleDisabledClick, {capture: true});
    super.disconnectedCallback();
  }

  /**
   * Capturing on the host runs ahead of every caller handler, whether the click targets the host,
   * slotted content, or the shadow content, so a disabled button swallows the click the way a
   * native disabled button does.
   */
  handleDisabledClick = (ev: MouseEvent) => {
    if (this.attr(`disabled`)) {
      ev.preventDefault();
      ev.stopImmediatePropagation();
    }
  };

  /** Enter and Space activate the focused host the way they activate a native button. */
  handleKeyDown = (ev: KeyboardEvent) => {
    if (ev.defaultPrevented || ev.composedPath()[0] !== this || this.attr(`disabled`) || this.attr(`not-focusable`)) {
      return;
    }
    if (ev.key === `Enter` || ev.key === ` `) {
      ev.preventDefault();
      this.getKeyboardActivationTarget().click();
    }
  };

  /** The element a keyboard activation clicks. Subclasses with a nested link activate that link. */
  protected getKeyboardActivationTarget(): HTMLElement {
    return this;
  }
}
